import { Check } from "@mui/icons-material";
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { FC } from "react";
import type { NurseFullRole } from "../../../utils/models";
import { getEmploymentTypeDisplayName, getShortRole } from "../../../utils/utilFunctions";
import { TABLE_HEADERS, TABLE_STYLES } from "../constants";
import type { ScheduleReportShift, SortField, SortOrder } from "../types";
import { formatTimestamp, formatTimestampText } from "../utils";

interface ScheduleTableProps {
	entries: ScheduleReportShift[];
	isLoading: boolean;
	hasShifts: boolean;
	sortField: SortField;
	sortOrder: SortOrder;
	currentPage: number;
	pageSize: number;
	onSort: (field: SortField) => void;
}

const SortableTableHeader: FC<{
	field: SortField;
	label: string;
	width: string;
	sortField: SortField;
	sortOrder: SortOrder;
	onSort: (field: SortField) => void;
}> = ({ field, label, width, sortField, sortOrder, onSort }) => (
	<TableCell sx={{ width }}>
		<TableSortLabel
			active={sortField === field}
			direction={sortField === field ? sortOrder : "asc"}
			onClick={() => onSort(field)}
			sx={(theme) => ({
				"& .MuiTableSortLabel-icon": {
					color: `${theme.palette.primary.dark} !important`,
				},
				color: theme.palette.primary.dark,
				fontWeight: "bold",
			})}
		>
			{label}
		</TableSortLabel>
	</TableCell>
);

export const ScheduleTable: FC<ScheduleTableProps> = ({ entries, isLoading, hasShifts, sortField, sortOrder, currentPage, pageSize, onSort }) => {
	const theme = useTheme();
	const renderCellContent = (entry: ScheduleReportShift, field: SortField | "#") => {
		switch (field) {
			case "#":
				return null;
			case "nurseName":
				return entry.nurseName;
			case "role":
				return getShortRole(entry.role as NurseFullRole) || entry.role;
			case "employmentType":
				return getEmploymentTypeDisplayName(entry.employmentType);
			case "startTime":
				return (
					<Tooltip title={formatTimestampText(entry.startTime)} arrow>
						<Box component="span">{formatTimestamp(entry.startTime)}</Box>
					</Tooltip>
				);
			case "endTime":
				return (
					<Tooltip title={formatTimestampText(entry.endTime)} arrow>
						<Box component="span">{formatTimestamp(entry.endTime)}</Box>
					</Tooltip>
				);
			case "location":
				return entry.location || "";
			case "agencyName":
				return entry.agencyName || "";
			case "charge":
				return entry.charge ? <Check sx={{ fontSize: 18, color: theme.palette.primary.dark }} /> : "";
			default:
				return "";
		}
	};

	return (
		<Box
			data-testid="schedule-table"
			sx={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
			}}
		>
			<TableContainer sx={TABLE_STYLES.container}>
				<Table size="small" stickyHeader sx={TABLE_STYLES.table}>
					<TableHead>
						<TableRow>
							{TABLE_HEADERS.map((header) =>
								header.field === "#" ? (
									<TableCell key={header.field} sx={{ width: header.width }}>
										{header.label}
									</TableCell>
								) : (
									<SortableTableHeader
										key={header.field}
										field={header.field}
										label={header.label}
										width={header.width}
										sortField={sortField}
										sortOrder={sortOrder}
										onSort={onSort}
									/>
								),
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={TABLE_HEADERS.length} sx={{ textAlign: "center", padding: "40px" }}>
									<CircularProgress sx={{ color: theme.palette.primary.dark }} />
								</TableCell>
							</TableRow>
						) : entries.length === 0 && !hasShifts ? (
							<TableRow>
								<TableCell colSpan={TABLE_HEADERS.length} sx={{ textAlign: "center", padding: "40px" }}>
									<Typography sx={{ color: theme.palette.primary.dark }}>
										No shifts found. Please select date range and click "Run Query" to load data.
									</Typography>
								</TableCell>
							</TableRow>
						) : entries.length === 0 && hasShifts ? (
							<TableRow>
								<TableCell colSpan={TABLE_HEADERS.length} sx={{ textAlign: "center", padding: "40px" }}>
									<Typography sx={{ color: theme.palette.primary.dark }}>No shifts match the current filters. Try adjusting your filters.</Typography>
								</TableCell>
							</TableRow>
						) : (
							entries.map((entry, index) => {
								const globalIndex = (currentPage - 1) * pageSize + index + 1;
								return (
									<TableRow key={entry.id || `${currentPage}-${index}`} hover sx={{ cursor: "pointer" }}>
										{TABLE_HEADERS.map((header) => (
											<TableCell key={header.field}>{header.field === "#" ? globalIndex : renderCellContent(entry, header.field)}</TableCell>
										))}
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
